<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import SectionTitle from '@/components/common/section-title.vue';
import { AnimatedContent } from '@/components/vuebits';

const { t } = useI18n();
const activeIndex = ref(0);
const scenarioNavRef = ref(null);

const scenarios = computed(() => [
  {
    index: '01',
    title: t('scenarios.1Title'),
    desc: t('scenarios.1Desc'),
    tag: t('scenarios.1Tag'),
  },
  {
    index: '02',
    title: t('scenarios.2Title'),
    desc: t('scenarios.2Desc'),
    tag: t('scenarios.2Tag'),
  },
  {
    index: '03',
    title: t('scenarios.3Title'),
    desc: t('scenarios.3Desc'),
    tag: t('scenarios.3Tag'),
  },
  {
    index: '04',
    title: t('scenarios.4Title'),
    desc: t('scenarios.4Desc'),
    tag: t('scenarios.4Tag'),
  },
]);

const current = computed(() => scenarios.value[activeIndex.value] ?? scenarios.value[0]);

function selectScenario(index) {
  activeIndex.value = index;
}

/** 只滚 tabs 横滑容器，不用 scrollIntoView（会带动整块横向位移） */
function scrollActiveScenarioTab() {
  if (typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches) return;
  requestAnimationFrame(() => {
    const nav = scenarioNavRef.value;
    if (!nav) return;
    const el = nav.querySelector(`[data-scenario-tab="${activeIndex.value}"]`);
    if (!el) return;
    const elRect = el.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();
    const nextLeft =
      nav.scrollLeft + (elRect.left - navRect.left) - (navRect.width - elRect.width) / 2;
    nav.scrollTo({
      left: Math.max(0, Math.min(nextLeft, nav.scrollWidth - nav.clientWidth)),
      behavior: 'smooth',
    });
  });
}

watch(activeIndex, () => {
  scrollActiveScenarioTab();
});

/** 仅桌面悬停切换，避免手机滚动误触 */
function onScenarioHover(index) {
  if (typeof window === 'undefined') return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  activeIndex.value = index;
}
</script>

<template>
  <section
    id="scenarios"
    class="relative overflow-hidden pb-[clamp(4rem,10vw,8rem)] pt-14 md:pt-20"
  >
    <div class="section-shell relative">
      <AnimatedContent :distance="24">
        <p class="eyebrow text-center">{{ t('scenarios.eyebrow') }}</p>
      </AnimatedContent>
      <SectionTitle :title="t('scenarios.title')" />
      <AnimatedContent :delay="0.05" :distance="20" class-name="-mt-7">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground sm:text-base md:text-lg">
          {{ t('scenarios.subtitle') }}
        </p>
      </AnimatedContent>

      <!-- tabs 不放进 AnimatedContent，避免 GSAP transform 导致无法横滑 -->
      <div class="mt-10 min-w-0 md:mt-14">
        <div class="grid min-w-0 items-start gap-8 md:gap-12 lg:grid-cols-12 lg:gap-16">
          <!-- 手机：横滑标签；min-w-0 防止 grid 被内容撑开导致无法内部滚动 -->
          <nav class="min-w-0 max-w-full md:hidden" :aria-label="t('scenarios.eyebrow')">
            <ul
              ref="scenarioNavRef"
              class="scenario-nav flex max-w-full gap-1 overflow-x-auto border-b border-slate-200/80 pb-px [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <li
                v-for="(item, index) in scenarios"
                :key="`m-${item.index}`"
                class="min-w-0 shrink-0"
              >
                <button
                  type="button"
                  class="relative w-auto min-w-[7.25rem] px-1 pb-3.5 text-left transition-colors"
                  :data-scenario-tab="index"
                  :aria-current="activeIndex === index ? 'true' : undefined"
                  @click="selectScenario(index)"
                >
                  <span
                    class="block text-[12px] font-medium tabular-nums tracking-wide"
                    :class="activeIndex === index ? 'text-primary' : 'text-slate-400'"
                  >
                    {{ item.index }}
                  </span>
                  <span
                    class="mt-1 block whitespace-nowrap text-[13px] font-semibold tracking-tight"
                    :class="activeIndex === index ? 'text-foreground' : 'text-slate-400'"
                  >
                    {{ item.title }}
                  </span>
                  <span
                    class="absolute inset-x-1 -bottom-px h-0.5 origin-left bg-primary transition-transform duration-300 ease-out"
                    :class="activeIndex === index ? 'scale-x-100' : 'scale-x-0'"
                  />
                </button>
              </li>
            </ul>
          </nav>

          <!-- 平板+：原索引列表 -->
          <nav class="hidden md:block lg:col-span-5" :aria-label="t('scenarios.eyebrow')">
            <ul class="border-t border-slate-200/80">
              <li
                v-for="(item, index) in scenarios"
                :key="item.index"
                class="border-b border-slate-200/80"
              >
                <button
                  type="button"
                  class="group flex w-full items-baseline gap-5 py-5 text-left transition-colors sm:gap-6 sm:py-6"
                  :aria-current="activeIndex === index ? 'true' : undefined"
                  @click="selectScenario(index)"
                  @mouseenter="onScenarioHover(index)"
                >
                  <span
                    class="w-8 shrink-0 text-[13px] font-medium tabular-nums tracking-wide transition-colors"
                    :class="activeIndex === index ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'"
                  >
                    {{ item.index }}
                  </span>
                  <span class="min-w-0 flex-1">
                    <span
                      class="block text-lg font-semibold tracking-tight transition-colors sm:text-xl"
                      :class="activeIndex === index ? 'text-foreground' : 'text-slate-400 group-hover:text-slate-700'"
                    >
                      {{ item.title }}
                    </span>
                    <span
                      class="mt-1 block text-xs font-medium tracking-wide text-slate-400 transition-colors"
                      :class="activeIndex === index ? 'text-primary/80' : ''"
                    >
                      {{ item.tag }}
                    </span>
                  </span>
                  <span
                    class="mt-1 h-px w-4 shrink-0 transition-all duration-300"
                    :class="activeIndex === index ? 'w-8 bg-primary' : 'bg-transparent group-hover:bg-slate-300'"
                    aria-hidden="true"
                  />
                </button>
              </li>
            </ul>
          </nav>

          <div class="min-w-0 lg:col-span-7">
            <Transition
              mode="out-in"
              enter-active-class="transition duration-300 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <div :key="current.index">
                <p class="text-sm font-medium text-primary">{{ current.tag }}</p>
                <h3 class="mt-3 text-2xl font-bold leading-tight tracking-tight text-foreground md:mt-4 md:text-3xl">
                  {{ current.title }}
                </h3>
                <p class="mt-4 max-w-xl text-[15px] leading-7 text-muted-foreground md:mt-5 md:text-base">
                  {{ current.desc }}
                </p>

                <a
                  href="#product"
                  class="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-[gap] hover:gap-2.5 md:mt-10"
                >
                  {{ t('scenarios.more') }}
                  <MaterialIcon name="arrow_forward" class="text-base" />
                </a>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.scenario-nav {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
}
.scenario-nav::-webkit-scrollbar {
  display: none;
}
</style>
